import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Grid,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Person as PersonIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Palette as PaletteIcon,
  Language as LanguageIcon,
  Info as InfoIcon,
  Book as BookIcon,
  VideoLibrary as VideoIcon,
  ContactSupport as SupportIcon,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { TOTPSetup } from './TOTPSetup';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-settings-tabpanel-${index}`}
      aria-labelledby={`user-settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const UserSettings: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    email: user?.email || '',
    phone: '',
    employeeId: user?.employee_id || '',
  });
  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    darkMode: false,
    language: 'en',
    timezone: 'UTC',
  });
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleProfileSave = () => {
    // TODO: Implement profile save functionality
    setEditMode(false);
  };

  const handleProfileCancel = () => {
    setProfileData({
      firstName: user?.first_name || '',
      lastName: user?.last_name || '',
      email: user?.email || '',
      phone: '',
      employeeId: user?.employee_id || '',
    });
    setEditMode(false);
  };

  const handleSettingsChange = (setting: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const helpTopics = [
    {
      title: 'Getting Started',
      icon: <InfoIcon />,
      content: 'Learn the basics of using Re-GenOps for power station operations management.',
      items: [
        'Dashboard Overview',
        'Navigation and Menus',
        'Site Selection',
        'User Roles and Permissions'
      ]
    },
    {
      title: 'Plant Information',
      icon: <BookIcon />,
      content: 'Understanding equipment status, KKS hierarchy, and technical specifications.',
      items: [
        'Equipment Overview',
        'KKS Code System',
        'Technical Specifications',
        'Equipment Status Monitoring'
      ]
    },
    {
      title: 'SCADA Operations',
      icon: <SettingsIcon />,
      content: 'Working with SCADA data, alarms, and real-time monitoring.',
      items: [
        'SCADA Historian',
        'Alarm Management',
        'Real-time Monitoring',
        'Data Trending'
      ]
    },
    {
      title: 'Permit Management',
      icon: <SecurityIcon />,
      content: 'Creating and managing work permits and isolations.',
      items: [
        'Creating Permits',
        'Permit Workflow',
        'Isolation Management',
        'Permit Approvals'
      ]
    },
    {
      title: 'Maintenance',
      icon: <SettingsIcon />,
      content: 'Managing maintenance tasks, schedules, and work orders.',
      items: [
        'Maintenance Dashboard',
        'Task Scheduling',
        'Work Orders',
        'Maintenance Records'
      ]
    },
    {
      title: 'Video Tutorials',
      icon: <VideoIcon />,
      content: 'Step-by-step video guides for common tasks.',
      items: [
        'Dashboard Navigation',
        'Creating Permits',
        'SCADA Monitoring',
        'Alarm Management'
      ]
    }
  ];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
        User Settings
      </Typography>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="user settings tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab 
              icon={<PersonIcon />} 
              label="Profile" 
              id="user-settings-tab-0"
              aria-controls="user-settings-tabpanel-0"
            />
            <Tab 
              icon={<SettingsIcon />} 
              label="Settings" 
              id="user-settings-tab-1"
              aria-controls="user-settings-tabpanel-1"
            />
            <Tab 
              icon={<SecurityIcon />} 
              label="Security" 
              id="user-settings-tab-2"
              aria-controls="user-settings-tabpanel-2"
            />
            <Tab 
              icon={<HelpIcon />} 
              label="Help & Support" 
              id="user-settings-tab-3"
              aria-controls="user-settings-tabpanel-3"
            />
          </Tabs>
        </Box>

        {/* Profile Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">Profile Information</Typography>
            <Box>
              {editMode ? (
                <>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleProfileSave}
                    sx={{ mr: 1 }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleProfileCancel}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setEditMode(true)}
                >
                  Edit Profile
                </Button>
              )}
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    mx: 'auto',
                    mb: 2,
                    bgcolor: 'primary.main',
                    fontSize: '3rem',
                  }}
                >
                  {user?.first_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
                </Avatar>
                <Typography variant="h6">
                  {user?.first_name} {user?.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user?.role}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={9}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={profileData.firstName}
                    onChange={(e) => setProfileData(prev => ({ ...prev, firstName: e.target.value }))}
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={profileData.lastName}
                    onChange={(e) => setProfileData(prev => ({ ...prev, lastName: e.target.value }))}
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={profileData.phone}
                    onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Employee ID"
                    value={profileData.employeeId}
                    onChange={(e) => setProfileData(prev => ({ ...prev, employeeId: e.target.value }))}
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={user?.username || ''}
                    disabled
                    helperText="Username cannot be changed"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Settings Tab */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h5" gutterBottom>Application Settings</Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Notifications
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.emailNotifications}
                        onChange={(e) => handleSettingsChange('emailNotifications', e.target.checked)}
                      />
                    }
                    label="Email Notifications"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.pushNotifications}
                        onChange={(e) => handleSettingsChange('pushNotifications', e.target.checked)}
                      />
                    }
                    label="Push Notifications"
                  />
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <PaletteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Appearance
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.darkMode}
                        onChange={(e) => handleSettingsChange('darkMode', e.target.checked)}
                      />
                    }
                    label="Dark Mode"
                  />
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <LanguageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Regional Settings
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    label="Language"
                    value={settings.language}
                    onChange={(e) => handleSettingsChange('language', e.target.value)}
                    sx={{ mb: 2 }}
                  >
                    <option value="en">English</option>
                    <option value="es">Español</option>
                    <option value="fr">Français</option>
                  </TextField>
                  <TextField
                    fullWidth
                    select
                    label="Timezone"
                    value={settings.timezone}
                    onChange={(e) => handleSettingsChange('timezone', e.target.value)}
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Denver">Mountain Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                  </TextField>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Security Tab */}
        <TabPanel value={tabValue} index={2}>
          <TOTPSetup />
          
        </TabPanel>

        {/* Help & Support Tab */}
        <TabPanel value={tabValue} index={3}>
          <Typography variant="h5" gutterBottom>Help & Support</Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {helpTopics.map((topic, index) => (
                <Accordion key={index} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {topic.icon}
                      <Typography sx={{ ml: 1, fontWeight: 500 }}>
                        {topic.title}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {topic.content}
                    </Typography>
                    <List dense>
                      {topic.items.map((item, itemIndex) => (
                        <ListItem key={itemIndex} sx={{ py: 0.5 }}>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <SupportIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Contact Support
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Need additional help? Contact our support team.
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<SupportIcon />}
                    onClick={() => setHelpDialogOpen(true)}
                  >
                    Contact Support
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Card>

      {/* Contact Support Dialog */}
      <Dialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Contact Support</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            For technical support or questions about Re-GenOps, please contact:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Email Support"
                secondary="support@regenops.com"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Phone Support"
                secondary="+1 (555) 123-4567"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Emergency Support"
                secondary="Available 24/7 for critical issues"
              />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 